import { createTheme, alpha } from '@mui/material/styles';
// MUI X Date Pickers theme augmentation
import type {} from '@mui/x-date-pickers/themeAugmentation';

// Warren-themed soft shadows - gentler, more approachable
const createCustomShadow = (color: string) => ({
  1: `0 2px 4px 0 ${alpha(color, 0.08)}, 0 1px 2px 0 ${alpha(color, 0.12)}`,
  2: `0 4px 8px 0 ${alpha(color, 0.1)}, 0 2px 4px 0 ${alpha(color, 0.08)}`,
  3: `0 8px 16px 0 ${alpha(color, 0.12)}, 0 3px 6px 0 ${alpha(color, 0.08)}`,
  4: `0 12px 24px 0 ${alpha(color, 0.14)}, 0 4px 8px 0 ${alpha(color, 0.1)}`,
  5: `0 16px 32px 0 ${alpha(color, 0.16)}, 0 6px 12px 0 ${alpha(color, 0.12)}`,
});

const shadows = createCustomShadow('#1e4d6e');

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e4d6e', // Warren's dark blue
      light: '#4a7ba0',
      dark: '#0f2a3d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5eb3d9', // Warren's sky blue
      light: '#8bcce6',
      dark: '#3a8bb3',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d84545',
      light: '#e57373',
      dark: '#b71c1c',
    },
    warning: {
      main: '#c19a6b', // Warren's tan/brown
      light: '#d4b896',
      dark: '#8b6f47',
    },
    success: {
      main: '#6b9b4f', // Warren's green
      light: '#8fb876',
      dark: '#4d7236',
    },
    info: {
      main: '#5eb3d9', // Warren's sky blue
      light: '#8bcce6',
      dark: '#3a8bb3',
    },
    grey: {
      50: '#faf9f7',
      100: '#f5f3f0',
      200: '#ebe8e3',
      300: '#ddd9d2',
      400: '#c9c3b8',
      500: '#a69d90',
      600: '#8b8074',
      700: '#6b5f51',
      800: '#4a4138',
      900: '#2d2720',
    },
    background: {
      default: '#faf9f7', // Warm cream background
      paper: '#ffffff',
    },
    text: {
      primary: '#2d2720', // Warm dark brown
      secondary: '#6b5f51',
    },
    divider: alpha('#6b5f51', 0.12),
  },
  typography: {
    fontFamily: '"Nunito", "Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800, // Bold, friendly headers
      letterSpacing: '-0.015em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '0em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600, // Slightly bolder buttons
      letterSpacing: '0.025em',
      textTransform: 'none',
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 400,
    },
  },
  shadows: [
    'none',
    shadows[1],
    shadows[1],
    shadows[2],
    shadows[2],
    shadows[3],
    shadows[3],
    shadows[3],
    shadows[4],
    shadows[4],
    shadows[4],
    shadows[4],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
    shadows[5],
  ],
  shape: {
    borderRadius: 16, // Warren's rounded, friendly aesthetic
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#adb5bd #f1f3f5',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: '#f1f3f5',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#adb5bd',
            border: '2px solid #f1f3f5',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#6c757d',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12, // More rounded for Warren's playful style
          padding: '10px 24px',
          transition: 'all 0.25s ease-in-out',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: shadows[2],
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1e4d6e 0%, #2a6b8f 100%)',
          color: '#ffffff',
          boxShadow: shadows[1],
          '&:hover': {
            background: 'linear-gradient(135deg, #0f2a3d 0%, #1e4d6e 100%)',
            color: '#ffffff',
            boxShadow: shadows[3],
          },
          '&:disabled': {
            background: '#a69d90',
            color: '#ffffff',
          },
        },
        outlined: {
          borderWidth: 2,
          borderRadius: 12,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: alpha('#1e4d6e', 0.06),
            transform: 'translateY(-2px)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          transition: 'box-shadow 0.3s ease-in-out',
          borderRadius: 16,
        },
        elevation0: {
          boxShadow: 'none',
          border: `1px solid ${alpha('#6b5f51', 0.12)}`,
        },
        elevation1: {
          boxShadow: shadows[1],
        },
        elevation2: {
          boxShadow: shadows[2],
        },
        elevation3: {
          boxShadow: shadows[3],
        },
        elevation4: {
          boxShadow: shadows[4],
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 20, // Extra rounded for Warren's playful style
          border: `1px solid ${alpha('#6b5f51', 0.12)}`,
          boxShadow: shadows[1],
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: shadows[3],
            transform: 'translateY(-4px)',
            borderColor: alpha('#1e4d6e', 0.2),
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1e4d6e 0%, #2a6b8f 100%)',
          boxShadow: shadows[2],
          borderBottom: `1px solid ${alpha('#000000', 0.12)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#faf9f7',
          borderRight: `1px solid ${alpha('#6b5f51', 0.12)}`,
          boxShadow: shadows[3],
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 12px',
          transition: 'all 0.25s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            transform: 'translateX(6px)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#5eb3d9', 0.15),
            borderLeft: `4px solid #1e4d6e`,
            '&:hover': {
              backgroundColor: alpha('#5eb3d9', 0.2),
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: shadows[1],
            },
            '&.Mui-focused': {
              boxShadow: shadows[2],
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: shadows[1],
            },
            '&.Mui-focused': {
              boxShadow: shadows[2],
            },
          },
        },
        icon: {
          color: '#6b5f51',
          transition: 'color 0.2s ease-in-out',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#5eb3d9', 0.15),
            color: '#1e4d6e',
            fontWeight: 700,
            '&:hover': {
              backgroundColor: alpha('#5eb3d9', 0.2),
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha('#5eb3d9', 0.1),
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#6b5f51',
          fontWeight: 600,
          fontSize: '0.9375rem',
          '&.Mui-focused': {
            color: '#1e4d6e',
            fontWeight: 700,
          },
          '&.Mui-error': {
            color: '#d84545',
          },
        },
        outlined: {
          transform: 'translate(14px, 16px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: '#ffffff',
            padding: '0 8px',
            marginLeft: -4,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 14,
          marginTop: 6,
          fontSize: '0.75rem',
          fontWeight: 500,
          '&.Mui-error': {
            color: '#d84545',
            fontWeight: 600,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#ffffff',
          transition: 'all 0.2s ease-in-out',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha('#1e4d6e', 0.3),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1e4d6e',
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d84545',
          },
        },
        notchedOutline: {
          borderColor: alpha('#6b5f51', 0.23),
          transition: 'border-color 0.2s ease-in-out',
        },
        input: {
          padding: '16px 14px',
          fontWeight: 500,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: '#6b5f51',
          '& .MuiTypography-root': {
            fontWeight: 600,
            color: '#6b5f51',
          },
        },
        positionStart: {
          marginRight: 12,
        },
        positionEnd: {
          marginLeft: 12,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#2d2720', 0.5),
          backdropFilter: 'blur(4px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 20, // Pill-shaped chips for Warren's playful style
          transition: 'all 0.2s ease-in-out',
        },
        filled: {
          '&:hover': {
            boxShadow: shadows[1],
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#faf9f7',
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: '#2d2720',
            borderBottom: `2px solid ${alpha('#6b5f51', 0.2)}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#5eb3d9', 0.06),
            boxShadow: `inset 0 0 0 1px ${alpha('#1e4d6e', 0.12)}`,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: shadows[1],
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: alpha('#6b9b4f', 0.12),
          color: '#4d7236',
          border: `1px solid ${alpha('#6b9b4f', 0.3)}`,
          '& .MuiAlert-icon': {
            color: '#6b9b4f',
          },
        },
        standardError: {
          backgroundColor: alpha('#d84545', 0.12),
          color: '#b71c1c',
          border: `1px solid ${alpha('#d84545', 0.3)}`,
          '& .MuiAlert-icon': {
            color: '#d84545',
          },
        },
        standardWarning: {
          backgroundColor: alpha('#c19a6b', 0.12),
          color: '#8b6f47',
          border: `1px solid ${alpha('#c19a6b', 0.3)}`,
          '& .MuiAlert-icon': {
            color: '#c19a6b',
          },
        },
        standardInfo: {
          backgroundColor: alpha('#5eb3d9', 0.12),
          color: '#3a8bb3',
          border: `1px solid ${alpha('#5eb3d9', 0.3)}`,
          '& .MuiAlert-icon': {
            color: '#5eb3d9',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: shadows[3],
          '&:hover': {
            boxShadow: shadows[4],
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20, // Extra rounded for Warren's playful style
          boxShadow: shadows[5],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: shadows[4],
          border: `1px solid ${alpha('#6b5f51', 0.12)}`,
          marginTop: 8,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1e4d6e',
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: shadows[2],
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: alpha('#6b5f51', 0.12),
        },
        bar: {
          borderRadius: 8,
          background: 'linear-gradient(90deg, #1e4d6e 0%, #5eb3d9 100%)',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#6b5f51', 0.12),
          borderRadius: 8,
        },
      },
    },
    // Form Controls - Rounded, friendly aesthetics
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 3,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-checked': {
            color: '#ffffff',
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': {
              backgroundColor: '#5eb3d9',
              opacity: 1,
              border: 'none',
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            boxShadow: `0 0 0 4px ${alpha('#5eb3d9', 0.3)}`,
          },
          '&.Mui-disabled': {
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              opacity: 0.3,
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: shadows[2],
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        track: {
          borderRadius: 13,
          backgroundColor: '#c9c3b8',
          opacity: 1,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: 10,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            transform: 'scale(1.1)',
          },
          '&.Mui-checked': {
            color: '#5eb3d9',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${alpha('#5eb3d9', 0.5)}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 10,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            transform: 'scale(1.1)',
          },
          '&.Mui-checked': {
            color: '#5eb3d9',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${alpha('#5eb3d9', 0.5)}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: -6,
          marginRight: 16,
          '& .MuiFormControlLabel-label': {
            fontWeight: 500,
            color: '#2d2720',
          },
          // Specific spacing for Switch controls with the smaller 42×26px size
          '&:has(.MuiSwitch-root)': {
            marginLeft: 0,
            gap: 16, // 16px gap between switch and label for optimal visual balance
          },
        },
      },
    },
    // Navigation Components
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
          borderBottom: `1px solid ${alpha('#6b5f51', 0.12)}`,
        },
        indicator: {
          height: 3,
          borderRadius: 3,
          background: 'linear-gradient(90deg, #1e4d6e 0%, #5eb3d9 100%)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        flexContainer: {
          gap: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 48,
          fontWeight: 600,
          fontSize: '0.9375rem',
          color: '#6b5f51',
          borderRadius: 12,
          margin: '4px 0',
          transition: 'all 0.25s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            color: '#1e4d6e',
          },
          '&.Mui-selected': {
            color: '#1e4d6e',
            backgroundColor: alpha('#5eb3d9', 0.1),
            fontWeight: 700,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha('#5eb3d9', 0.15),
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        separator: {
          color: '#a69d90',
          marginLeft: 8,
          marginRight: 8,
        },
        li: {
          '& .MuiLink-root': {
            fontWeight: 500,
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#1e4d6e',
          textDecorationColor: alpha('#1e4d6e', 0.4),
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            color: '#5eb3d9',
            textDecorationColor: '#5eb3d9',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${alpha('#5eb3d9', 0.5)}`,
            outlineOffset: 2,
            borderRadius: 4,
          },
        },
      },
    },
    // Feedback Components
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#1e4d6e',
            borderRadius: 16,
            boxShadow: shadows[4],
            fontWeight: 600,
            minWidth: 320,
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#1e4d6e',
        },
        colorPrimary: {
          color: '#1e4d6e',
        },
        colorSecondary: {
          color: '#5eb3d9',
        },
        svg: {
          filter: `drop-shadow(0 2px 4px ${alpha('#1e4d6e', 0.2)})`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.25s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            transform: 'scale(1.1)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${alpha('#5eb3d9', 0.5)}`,
            outlineOffset: 2,
          },
        },
        colorPrimary: {
          color: '#1e4d6e',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.12),
          },
        },
        colorSecondary: {
          color: '#5eb3d9',
          '&:hover': {
            backgroundColor: alpha('#5eb3d9', 0.12),
          },
        },
      },
    },
    // Display Components
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          fontSize: '0.75rem',
          minWidth: 20,
          height: 20,
          borderRadius: 10,
          padding: '0 6px',
          boxShadow: `0 0 0 2px #ffffff, ${shadows[2]}`,
        },
        colorPrimary: {
          background: 'linear-gradient(135deg, #1e4d6e 0%, #2a6b8f 100%)',
        },
        colorSecondary: {
          background: 'linear-gradient(135deg, #5eb3d9 0%, #8bcce6 100%)',
        },
        colorError: {
          background: 'linear-gradient(135deg, #d84545 0%, #e57373 100%)',
        },
        colorSuccess: {
          background: 'linear-gradient(135deg, #6b9b4f 0%, #8fb876 100%)',
        },
        colorWarning: {
          background: 'linear-gradient(135deg, #c19a6b 0%, #d4b896 100%)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1rem',
          backgroundColor: '#1e4d6e',
          color: '#ffffff',
          border: `2px solid #ffffff`,
          boxShadow: shadows[2],
        },
        colorDefault: {
          backgroundColor: alpha('#5eb3d9', 0.2),
          color: '#1e4d6e',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha('#6b5f51', 0.12),
          '&::before, &::after': {
            borderColor: alpha('#6b5f51', 0.12),
          },
        },
        textAlignLeft: {
          '&::before': {
            width: '5%',
          },
        },
        textAlignRight: {
          '&::after': {
            width: '5%',
          },
        },
        wrapper: {
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#6b5f51',
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    // Stepper Components
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '24px 0',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600,
          fontSize: '0.9375rem',
          '&.Mui-active': {
            color: '#1e4d6e',
            fontWeight: 700,
          },
          '&.Mui-completed': {
            color: '#6b9b4f',
            fontWeight: 600,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: '2rem',
          color: alpha('#6b5f51', 0.3),
          transition: 'all 0.3s ease-in-out',
          '&.Mui-active': {
            color: '#5eb3d9',
            transform: 'scale(1.15)',
            filter: `drop-shadow(0 2px 8px ${alpha('#5eb3d9', 0.4)})`,
          },
          '&.Mui-completed': {
            color: '#6b9b4f',
          },
        },
        text: {
          fill: '#ffffff',
          fontWeight: 700,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: alpha('#6b5f51', 0.3),
          borderTopWidth: 2,
          transition: 'border-color 0.3s ease-in-out',
        },
        root: {
          '&.Mui-active .MuiStepConnector-line': {
            borderColor: '#5eb3d9',
          },
          '&.Mui-completed .MuiStepConnector-line': {
            borderColor: '#6b9b4f',
          },
        },
      },
    },
    // Accordion Components
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${alpha('#6b5f51', 0.12)}`,
          boxShadow: shadows[1],
          marginBottom: 12,
          transition: 'all 0.3s ease-in-out',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '12px 0',
            boxShadow: shadows[3],
            borderColor: alpha('#1e4d6e', 0.2),
          },
        },
        rounded: {
          '&:first-of-type': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
          '&:last-of-type': {
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 16,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#5eb3d9', 0.06),
          },
          '&.Mui-expanded': {
            minHeight: 56,
            backgroundColor: alpha('#5eb3d9', 0.1),
          },
        },
        content: {
          margin: '16px 0',
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
        expandIconWrapper: {
          color: '#1e4d6e',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-expanded': {
            transform: 'rotate(180deg)',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          borderTop: `1px solid ${alpha('#6b5f51', 0.12)}`,
        },
      },
    },
    // Toggle Button Components
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#6b5f51', 0.08),
          borderRadius: 12,
          padding: 4,
          gap: 4,
        },
        grouped: {
          margin: 0,
          border: 'none',
          '&:not(:first-of-type)': {
            borderRadius: 12,
          },
          '&:first-of-type': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '8px 20px',
          color: '#6b5f51',
          transition: 'all 0.25s ease-in-out',
          border: 'none',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.12),
            color: '#1e4d6e',
          },
          '&.Mui-selected': {
            backgroundColor: '#ffffff',
            color: '#1e4d6e',
            boxShadow: shadows[1],
            fontWeight: 700,
            '&:hover': {
              backgroundColor: '#ffffff',
              boxShadow: shadows[2],
            },
          },
          '&.Mui-disabled': {
            border: 'none',
          },
        },
      },
    },
    // Slider Component
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 6,
          padding: '15px 0',
        },
        rail: {
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha('#6b5f51', 0.2),
        },
        track: {
          height: 6,
          borderRadius: 3,
          background: 'linear-gradient(90deg, #1e4d6e 0%, #5eb3d9 100%)',
          border: 'none',
        },
        thumb: {
          width: 20,
          height: 20,
          backgroundColor: '#ffffff',
          border: '3px solid #5eb3d9',
          boxShadow: shadows[2],
          transition: 'all 0.2s ease-in-out',
          '&:hover, &.Mui-focusVisible': {
            boxShadow: `0 0 0 8px ${alpha('#5eb3d9', 0.16)}`,
            transform: 'scale(1.2)',
          },
          '&.Mui-active': {
            boxShadow: `0 0 0 12px ${alpha('#5eb3d9', 0.24)}`,
          },
        },
        valueLabel: {
          backgroundColor: '#1e4d6e',
          borderRadius: 8,
          padding: '4px 8px',
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        mark: {
          width: 4,
          height: 4,
          borderRadius: 2,
          backgroundColor: '#ffffff',
        },
        markActive: {
          backgroundColor: '#ffffff',
        },
        markLabel: {
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#6b5f51',
        },
      },
    },
    // Autocomplete Component
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            padding: '6px 12px',
          },
        },
        paper: {
          borderRadius: 12,
          boxShadow: shadows[4],
          border: `1px solid ${alpha('#6b5f51', 0.12)}`,
          marginTop: 8,
        },
        option: {
          borderRadius: 8,
          margin: '4px 8px',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
          '&[aria-selected="true"]': {
            backgroundColor: alpha('#5eb3d9', 0.15),
            color: '#1e4d6e',
            fontWeight: 700,
          },
          '&.Mui-focused': {
            backgroundColor: alpha('#1e4d6e', 0.08),
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: alpha('#5eb3d9', 0.2),
          },
        },
        tag: {
          borderRadius: 8,
          fontWeight: 600,
        },
        endAdornment: {
          '& .MuiIconButton-root': {
            borderRadius: 8,
          },
        },
        clearIndicator: {
          color: '#6b5f51',
          '&:hover': {
            backgroundColor: alpha('#d84545', 0.12),
            color: '#d84545',
          },
        },
        popupIndicator: {
          color: '#6b5f51',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.12),
            color: '#1e4d6e',
          },
        },
      },
    },
    // Pagination Component
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            fontWeight: 600,
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha('#1e4d6e', 0.08),
              transform: 'scale(1.1)',
            },
            '&.Mui-selected': {
              backgroundColor: '#5eb3d9',
              color: '#ffffff',
              fontWeight: 700,
              boxShadow: shadows[1],
              '&:hover': {
                backgroundColor: '#3a8bb3',
                boxShadow: shadows[2],
              },
            },
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 12,
          transition: 'all 0.2s ease-in-out',
        },
        outlined: {
          border: `1px solid ${alpha('#6b5f51', 0.2)}`,
          '&.Mui-selected': {
            border: 'none',
          },
        },
      },
    },
    // Rating Component
    MuiRating: {
      styleOverrides: {
        root: {
          color: '#c19a6b', // Warren's tan/brown for stars
        },
        iconFilled: {
          color: '#c19a6b',
        },
        iconHover: {
          color: '#d4b896',
          transform: 'scale(1.2)',
          transition: 'all 0.2s ease-in-out',
        },
        iconEmpty: {
          color: alpha('#c19a6b', 0.3),
        },
        decimal: {
          color: '#c19a6b',
        },
      },
    },
    // MUI X Date Pickers - Warren themed date selection
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          overflow: 'hidden',
        },
        contentWrapper: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 12,
          paddingBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        label: {
          fontWeight: 700,
          fontSize: '1rem',
          color: '#1e4d6e',
        },
        switchViewButton: {
          color: '#1e4d6e',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#1e4d6e', 0.08),
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontWeight: 700,
          fontSize: '0.875rem',
          color: '#6b5f51',
          height: 36,
          width: 36,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          fontWeight: 600,
          borderRadius: 12,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#5eb3d9', 0.12),
            transform: 'scale(1.1)',
          },
          '&:focus': {
            backgroundColor: alpha('#5eb3d9', 0.2),
          },
          '&.Mui-selected': {
            backgroundColor: '#5eb3d9',
            color: '#ffffff',
            fontWeight: 700,
            boxShadow: shadows[2],
            '&:hover': {
              backgroundColor: '#3a8bb3',
              boxShadow: shadows[3],
            },
            '&:focus': {
              backgroundColor: '#5eb3d9',
            },
          },
          '&.MuiPickersDay-today': {
            border: `2px solid #1e4d6e`,
            fontWeight: 700,
            '&:not(.Mui-selected)': {
              backgroundColor: alpha('#1e4d6e', 0.08),
            },
          },
        },
        dayOutsideMonth: {
          color: alpha('#6b5f51', 0.4),
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          width: '100%',
          '& button': {
            fontSize: '0.9375rem',
            fontWeight: 600,
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha('#5eb3d9', 0.12),
              transform: 'scale(1.05)',
            },
            '&:focus': {
              backgroundColor: alpha('#5eb3d9', 0.2),
            },
            '&.Mui-selected': {
              backgroundColor: '#5eb3d9',
              color: '#ffffff',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#3a8bb3',
              },
              '&:focus': {
                backgroundColor: '#5eb3d9',
              },
            },
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          width: '100%',
          '& button': {
            fontSize: '0.9375rem',
            fontWeight: 600,
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha('#5eb3d9', 0.12),
              transform: 'scale(1.05)',
            },
            '&:focus': {
              backgroundColor: alpha('#5eb3d9', 0.2),
            },
            '&.Mui-selected': {
              backgroundColor: '#5eb3d9',
              color: '#ffffff',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#3a8bb3',
              },
              '&:focus': {
                backgroundColor: '#5eb3d9',
              },
            },
          },
        },
      },
    },
    MuiDatePickerToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#1e4d6e', 0.04),
          borderBottom: `1px solid ${alpha('#6b5f51', 0.12)}`,
          paddingLeft: 24,
          paddingRight: 24,
        },
        title: {
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#6b5f51',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    },
    MuiTimeClock: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
        },
        arrowSwitcher: {
          '& button': {
            color: '#1e4d6e',
            '&:hover': {
              backgroundColor: alpha('#1e4d6e', 0.08),
            },
          },
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '1rem',
          color: '#2d2720',
          '&.Mui-selected': {
            backgroundColor: '#5eb3d9',
            color: '#ffffff',
            fontWeight: 700,
          },
          '&:hover': {
            backgroundColor: alpha('#5eb3d9', 0.12),
          },
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: '#5eb3d9',
        },
        thumb: {
          backgroundColor: '#5eb3d9',
          border: `4px solid #ffffff`,
          boxShadow: shadows[2],
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root': {
            borderRadius: 8,
            margin: '4px 8px',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha('#5eb3d9', 0.12),
            },
            '&.Mui-selected': {
              backgroundColor: '#5eb3d9',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#3a8bb3',
              },
            },
          },
        },
      },
    },
  },
});
